import { CheckIcon } from 'lucide-react';
import { type ReactNode, useId, useState } from 'react';
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList
} from '@/components/ui/command';
import {
  Popover,
  PopoverContent,
  PopoverTrigger
} from '@/components/ui/popover';
import { cn } from '@/lib/utils';

export type ComboboxOption = {
  value: string;
  label: string;
  /** Optional custom render for the option row */
  render?: ReactNode;
};

type ComboboxPopoverProps = {
  /** The trigger element (rendered as a button inside the pill) */
  trigger: ReactNode;
  /**
   * Accessible name for the trigger button. Required because `trigger` may
   * be a visual-only chip with no readable text.
   */
  ariaLabel: string;
  /** Available options */
  options: ComboboxOption[];
  /** Currently selected value(s) */
  selected: string[];
  /** Callback when selection changes */
  onSelect: (value: string) => void;
  /** Whether multiple selections are allowed */
  multi?: boolean;
  /** Whether to show a search input */
  searchable?: boolean;
  /** Placeholder for the search input */
  searchPlaceholder?: string;
  /** Message when no results match the search */
  emptyMessage?: string;
  /** Width class for the popover content */
  width?: string;
  /**
   * Whether to open the popover automatically on mount. Honored once on
   * mount; subsequent prop changes are ignored.
   */
  defaultOpen?: boolean;
};

export const ComboboxPopover = ({
  trigger,
  ariaLabel,
  options,
  selected,
  onSelect,
  multi = false,
  searchable = false,
  searchPlaceholder = 'Search...',
  emptyMessage = 'No results found.',
  width = 'w-48',
  defaultOpen = false
}: ComboboxPopoverProps) => {
  const [open, setOpen] = useState(defaultOpen);
  const listboxId = useId();

  const handleSelect = (value: string) => {
    onSelect(value);
    if (!multi) {
      setOpen(false);
    }
  };

  return (
    <Popover onOpenChange={setOpen} open={open}>
      <PopoverTrigger asChild>
        <button
          aria-controls={listboxId}
          aria-expanded={open}
          aria-haspopup="listbox"
          aria-label={ariaLabel}
          className="flex h-full items-center gap-1.5 px-2 hover:bg-accent"
          type="button"
        >
          {trigger}
        </button>
      </PopoverTrigger>
      <PopoverContent align="start" className={cn(width, 'p-0')}>
        <Command className="border-none shadow-none">
          {searchable && <CommandInput placeholder={searchPlaceholder} />}
          <CommandList aria-label={ariaLabel} id={listboxId}>
            <CommandEmpty>{emptyMessage}</CommandEmpty>
            <CommandGroup>
              {options.map(option => {
                const isSelected = selected.includes(option.value);
                return (
                  <CommandItem
                    className="cursor-pointer"
                    key={option.value}
                    onSelect={() => handleSelect(option.value)}
                  >
                    {multi && (
                      <div
                        className={cn(
                          'mr-2 flex size-4 items-center justify-center rounded-sm border border-primary',
                          isSelected
                            ? 'bg-primary text-primary-foreground'
                            : '[&_svg]:invisible'
                        )}
                      >
                        <CheckIcon className="size-3" />
                      </div>
                    )}
                    {option.render ?? (
                      <span className="truncate">{option.label}</span>
                    )}
                  </CommandItem>
                );
              })}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
};
